import * as React from "react";
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useApiClient } from "../../../api";
import GeneralLoading from "../../../shared-components/Loading";
import { usePagination } from "../../../hooks/usePagination";
import { formatEntityType } from "../../question/utils";

const CanonicalEntityAutocomplete = ({
  entity,
  selectedEntityType,
  onChange,
  selectRequired = false,
}) => {
  const [inputValue, setInputValue] = useState("");

  const [debouncedValue, setDebouncedValue] = useState(inputValue);

  const ApiClient = useApiClient();

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, 200);

    return () => clearTimeout(handler);
  }, [inputValue]);

  const filters = useMemo(
    () => ({
      searchString: debouncedValue,
      entityType: selectedEntityType,
    }),
    [debouncedValue],
  );

  const { options, loading } = usePagination(
    ({ first, after, ...filters }) =>
      ApiClient.skill.searchCanonicalEntities({ first, after, ...filters }),
    20,
    filters,
  );

  useEffect(() => {
    if (!entity) {
      setInputValue("");
    }
  }, [entity]);

  const handleChange = (_, newValue) => {
    onChange(newValue || null);
  };

  return (
    <Autocomplete
      sx={{ width: "100%" }}
      value={entity}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue);
      }}
      filterOptions={(x) => x}
      options={options}
      loading={loading}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      getOptionLabel={(option) => option?.label || ""}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Typography variant="body1">{option.label}</Typography>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={
            selectRequired
              ? "Select an entity"
              : "Select an entity (if possible)"
          }
          variant="outlined"
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && (
                  <GeneralLoading
                    direction="row"
                    size={25}
                    message="Searching..."
                    sx={{ mr: 1 }}
                  />
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

const EntityTypes = ["knowledge_gap", "skill", "misconception"];

const EntityTypePicker = ({ selectedEntityType, setSelectedEntityType }) => {
  return (
    <FormControl fullWidth>
      <InputLabel id="entity-type">Entity Type</InputLabel>
      <Select
        labelId="entity-type"
        label={"Evidence Type"}
        value={selectedEntityType || ""}
        onChange={(e) => setSelectedEntityType(e.target.value)}
      >
        {EntityTypes.map((entityType) => (
          <MenuItem key={entityType} value={entityType}>
            {formatEntityType(entityType)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export const LinkCanonicalEntityDialog = ({
  open,
  setOpen,
  onConfirm,
  entityRequired = false,
}) => {
  const [entity, setEntity] = useState("");
  const [selectedEntityType, setSelectedEntityType] = useState("");
  const [reasoning, setReasoning] = useState("");

  const handleConfirmClicked = () => {
    onConfirm({
      entity,
      selectedEntityType,
      reasoning: reasoning.trim() || "",
    });
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      slotProps={{
        paper: {
          component: "form",
          sx: { backgroundImage: "none" },
        },
      }}
    >
      <DialogTitle>Add Learning Evidence</DialogTitle>
      <DialogContent>
        <Stack direction={"column"} spacing={2} sx={{ mt: 2 }}>
          <EntityTypePicker
            selectedEntityType={selectedEntityType}
            setSelectedEntityType={setSelectedEntityType}
          />
          <CanonicalEntityAutocomplete
            entity={entity}
            selectedEntityType={selectedEntityType}
            onChange={(value) => {
              setEntity(value); // This was missing!
            }}
            selectRequired={entityRequired}
          />
          <TextField
            multiline
            rows={2}
            label={"Evidence or reasoning"}
            placeholder="A brief description of the evidence or reasoning"
            value={reasoning}
            onChange={(e) => setReasoning(e.target.value)}
            style={{
              minWidth: "100%",
              borderRadius: "4px",
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)} variant={"outlined"}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirmClicked}
          variant={"contained"}
          disabled={
            !reasoning.trim() ||
            !selectedEntityType.trim() ||
            (!entity && entityRequired)
          }
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};
